import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ShepherdService } from 'angular-shepherd';
import Step from 'shepherd.js/src/types/step';

import { Logger } from '../logger.service';
import { Tour, TourPlacement, TourStep } from './guided-tour.model';

const log = new Logger('GuidedTour');

/** Prefijo de la marca de «ya lo vi» en localStorage. */
const SEEN_PREFIX = 'tour';

/** Versión de las marcas: subirla vuelve a ofrecer los autoarranques. */
const SEEN_VERSION = 'v2';

/**
 * Espera a que aparezca el ancla antes de mostrar el paso. Es corta a propósito: lo
 * que de verdad tarda —abrir un diálogo, entrar al detalle— lo resuelven las puertas
 * (`waitFor`), no un bloqueo con reloj. Un paso que espera segundos en blanco se
 * siente colgado.
 */
const DEFAULT_WAIT_MS = 400;

/** Un paso que además navega necesita más: la pantalla nueva tiene que montarse. */
const ROUTE_WAIT_MS = 1500;

/** Cada cuánto se busca el ancla que todavía no existe. */
const POLL_INTERVAL_MS = 120;

/**
 * Tras detectar el elemento esperado, se le da un respiro para que el diálogo termine
 * de animarse: si el globo se ancla a media animación queda en el lugar equivocado.
 */
const SETTLE_MS = 350;

/**
 * Lo que tarda la librería en calcular dónde va el globo. Hasta entonces se mantiene
 * escondido: si no, se le ve aparecer en la esquina y saltar a su lugar.
 */
const PLACEMENT_MS = 120;

/**
 * Margen antes de dar por perdido el elemento de un paso con `requires`. Sin él, un
 * repintado de Angular bastaría para mandar el tour de regreso.
 */
const LOST_GRACE_MS = 700;

/**
 * Motor de los tours guiados, sobre **Shepherd** (`angular-shepherd`).
 *
 * Aquí se traduce el modelo propio (`Tour` / `TourStep`) a pasos de Shepherd: el velo
 * con recorte, el posicionado del globo, el scroll y el teclado los lleva la
 * librería. Lo propio es lo que Shepherd no sabe:
 *
 * - **navegar** a la ruta del paso;
 * - **esperar a la persona** en los pasos puerta (`waitFor` / `waitForGone`), que
 *   avanzan solos en cuanto se abre el diálogo o se entra al detalle;
 * - **regresar** a esa puerta si el elemento del paso desaparece (`requires`);
 * - recordar qué tours ya se vieron.
 */
@Injectable({ providedIn: 'root' })
export class GuidedTourService {
    /** Vigilancia del paso en curso (puerta o `requires`). */
    private watcher?: number;

    /**
     * Última puerta mostrada. Si un paso pierde su elemento, ahí es donde tiene
     * sentido volver: es el paso que dice qué hay que abrir.
     */
    private lastGateId?: string;

    /** Hacia dónde se movió la persona, para no re-brincar una puerta al retroceder. */
    private direction: 'forward' | 'back' = 'forward';

    constructor(
        private shepherd: ShepherdService,
        private router: Router,
    ) {}

    get isActive(): boolean {
        return this.shepherd.isActive;
    }

    start(tour: Tour): void {
        const steps = tour.steps.filter((step) => (step.when ? step.when() : true));
        if (!steps.length) {
            log.warn(`El tour "${tour.id}" no tiene pasos aplicables`);
            return;
        }

        if (this.shepherd.isActive) {
            this.shepherd.cancel();
        }

        this.clearWatch();
        this.lastGateId = undefined;
        this.direction = 'forward';

        this.markSeen(tour.id);
        this.configure(tour);
        this.shepherd.addSteps(
            steps.map((step, index) => this.toShepherdStep(tour, step, index, steps.length)),
        );
        // Al salir con Esc o con la ✕, Shepherd destruye los pasos sin pasar por su
        // `hide`: sin esto quedaría girando la vigilancia del paso en curso.
        this.shepherd.tourObject?.on('inactive', () => this.clearWatch());
        this.shepherd.start();
    }

    /**
     * Arranca el tour la primera vez que la persona entra. Si sale a medias también
     * cuenta como visto: no se insiste.
     */
    startIfUnseen(tour: Tour): void {
        if (this.isActive || this.isSeen(tour.id)) {
            return;
        }
        if (tour.when && !tour.when()) {
            return;
        }
        this.start(tour);
    }

    stop(): void {
        this.clearWatch();
        if (this.shepherd.isActive) {
            this.shepherd.cancel();
        }
    }

    isSeen(tourId: string): boolean {
        try {
            return localStorage.getItem(this.seenKey(tourId)) === '1';
        } catch {
            // Modo privado o storage bloqueado: se ofrece el tour, no se recuerda.
            return false;
        }
    }

    markSeen(tourId: string): void {
        try {
            localStorage.setItem(this.seenKey(tourId), '1');
        } catch {
            log.debug('No se pudo recordar el tour visto');
        }
    }

    /** Vuelve a habilitar los autoarranques de una lista de tours. */
    forget(tourIds: string[]): void {
        tourIds.forEach((tourId) => {
            try {
                localStorage.removeItem(this.seenKey(tourId));
            } catch {
                // Sin storage no hay nada que olvidar.
            }
        });
    }

    // ------------------------------------------------------------------
    // Traducción a Shepherd
    // ------------------------------------------------------------------

    private configure(tour: Tour): void {
        this.shepherd.modal = true;
        this.shepherd.keyboardNavigation = true;
        this.shepherd.exitOnEsc = true;
        this.shepherd.confirmCancel = false;
        this.shepherd.tourName = tour.id;
        this.shepherd.defaultStepOptions = {
            // `avz-tour` es el gancho de estilos: el tema vive en
            // `guided-tour.theme.scss`, global porque Shepherd pinta en el body. El
            // nombre es del sistema, no de un módulo: lo usan todos los tours.
            classes: 'avz-tour',
            cancelIcon: { enabled: true, label: 'Salir del tour' },
            // Instantáneo a propósito: con scroll suave, Shepherd calcula la posición
            // del globo mientras la página todavía se está moviendo y lo deja
            // colgado en la esquina.
            scrollTo: { behavior: 'auto', block: 'center' },
            modalOverlayOpeningPadding: 6,
            modalOverlayOpeningRadius: 8,
            // La pantalla se sigue usando durante el tour: hay pasos que piden abrir
            // un diálogo o un documento para continuar.
            canClickTarget: true,
        };
    }

    private toShepherdStep(
        tour: Tour,
        step: TourStep,
        index: number,
        total: number,
    ): Step.StepOptions {
        const isFirst = index === 0;
        const isLast = index === total - 1;
        const id = `${tour.id}-${index}`;
        const isGate = !!(step.waitFor || step.waitForGone);

        return {
            id,
            title: step.title,
            // Función y no cadena: se evalúa cada vez que el paso se muestra, que es
            // cuando se puede saber si la pantalla está vacía.
            text: () => this.buildText(tour, step, index + 1, total, isFirst, isGate),
            classes: isGate ? 'avz-tour avz-tour--gate' : 'avz-tour',
            // Lo que hay que tocar se marca más fuerte que un resaltado normal.
            highlightClass: isGate ? 'tour-gate-target' : undefined,
            attachTo: step.anchor
                ? {
                      // Se resuelve al mostrar el paso, no al armar el tour: el
                      // elemento puede aparecer después.
                      element: () => this.findAnchor(step.anchor as string) ?? undefined,
                      on: this.placement(step.placement),
                  }
                : undefined,
            beforeShowPromise: () => this.prepare(step),
            when: {
                show: () => this.onShow(id, step, isFirst),
                hide: () => this.clearWatch(),
            },
            buttons: this.buttons(isFirst, isLast, isGate),
        };
    }

    /**
     * Las acciones van explícitas en vez de los `type` de `angular-shepherd`: su
     * mapeo no está en los tipos de Shepherd y así el botón queda tipado.
     *
     * En una puerta el botón principal no es «Siguiente» sino «Omitir»: el avance
     * normal lo dispara la persona al abrir lo que se le pide, y el botón está para
     * que nunca quede atrapada si decide no hacerlo.
     */
    private buttons(
        isFirst: boolean,
        isLast: boolean,
        isGate: boolean,
    ): Step.StepOptionsButton[] {
        const buttons: Step.StepOptionsButton[] = [
            {
                text: 'Salir',
                classes: 'tour-button tour-button--ghost',
                action: () => this.shepherd.cancel(),
            },
        ];
        if (!isFirst) {
            buttons.push({
                text: 'Anterior',
                classes: 'tour-button tour-button--secondary',
                action: () => {
                    this.direction = 'back';
                    this.shepherd.back();
                },
            });
        }
        if (isGate && !isLast) {
            buttons.push({
                text: 'Omitir',
                classes: 'tour-button tour-button--secondary',
                action: () => {
                    this.direction = 'forward';
                    this.shepherd.next();
                },
            });
            return buttons;
        }
        buttons.push({
            text: isLast ? 'Listo' : 'Siguiente',
            classes: 'tour-button tour-button--primary',
            action: () => {
                this.direction = 'forward';
                return isLast ? this.shepherd.complete() : this.shepherd.next();
            },
        });
        return buttons;
    }

    // ------------------------------------------------------------------
    // Puertas y vigilancia
    // ------------------------------------------------------------------

    /**
     * Al mostrarse un paso arranca su vigilancia, que hace tres cosas en el mismo
     * latido:
     *
     * 1. **cuida el globo** — si el elemento señalado desaparece de golpe (se cerró
     *    el diálogo), la librería recalcula la posición contra un elemento que ya no
     *    está en el documento y lo manda a la esquina superior izquierda. Antes de
     *    que eso se vea, se esconde;
     * 2. **abre la puerta** cuando aparece (o desaparece) lo que el paso espera;
     * 3. **regresa a la puerta** si el paso perdió lo que necesitaba.
     */
    private onShow(id: string, step: TourStep, isFirst: boolean): void {
        this.clearWatch();

        const isGate = !!(step.waitFor || step.waitForGone);
        if (isGate) {
            this.lastGateId = id;
        }

        // El globo se pinta antes de que la librería calcule dónde va: si se muestra
        // desde el primer cuadro, se ve aparecer en la esquina y saltar a su lugar.
        this.hideBalloon();
        window.setTimeout(() => this.showBalloon(), PLACEMENT_MS);

        // Un ancla que nunca existió no es un elemento perdido: el paso simplemente
        // sale centrado, y así se queda. Sólo se vigila lo que sí estaba.
        const anchored = !!step.anchor && !!this.findAnchor(step.anchor);
        const goingBack = isGate && this.direction === 'back' && !isFirst;
        let missingMs = 0;

        this.watcher = window.setInterval(() => {
            if (!this.shepherd.isActive) {
                this.clearWatch();
                return;
            }

            // 1. El globo sigue a su elemento: si no está, no se enseña.
            const anchorPresent = anchored
                ? !!this.findAnchor(step.anchor as string)
                : true;
            if (anchored) {
                if (anchorPresent) {
                    this.showBalloon();
                } else {
                    this.hideBalloon();
                }
            }

            // 2. La puerta.
            if (isGate) {
                if (!this.gateIsOpen(step)) {
                    return;
                }
                this.clearWatch();
                // El diálogo recién abierto todavía se está animando.
                window.setTimeout(() => {
                    if (!this.shepherd.isActive) {
                        return;
                    }
                    if (goingBack) {
                        this.shepherd.back();
                    } else {
                        this.direction = 'forward';
                        this.shepherd.next();
                    }
                }, SETTLE_MS);
                return;
            }

            // 3. Lo que el paso necesita para tener sentido.
            if (!step.requires) {
                return;
            }
            if (this.findAnchor(step.requires)) {
                missingMs = 0;
                return;
            }
            missingMs += POLL_INTERVAL_MS;
            if (missingMs < LOST_GRACE_MS) {
                return;
            }
            this.clearWatch();
            // Hacia adelante: la puerta volverá a avanzar sola en cuanto se reabra.
            this.direction = 'forward';
            if (this.lastGateId) {
                this.shepherd.show(this.lastGateId);
            } else {
                this.shepherd.back();
            }
        }, POLL_INTERVAL_MS);
    }

    private gateIsOpen(step: TourStep): boolean {
        if (step.waitForGone) {
            return !this.findAnchor(step.waitForGone);
        }
        return !!this.findAnchor(step.waitFor as string);
    }

    /**
     * Esconder y enseñar el globo se hace con una clase y no destruyendo el paso: la
     * librería sigue siendo la dueña de su ciclo de vida.
     */
    private hideBalloon(): void {
        this.balloon()?.classList.add('tour-balloon--hidden');
    }

    private showBalloon(): void {
        this.balloon()?.classList.remove('tour-balloon--hidden');
    }

    private balloon(): HTMLElement | undefined {
        return this.shepherd.tourObject?.getCurrentStep()?.getElement() ?? undefined;
    }

    private clearWatch(): void {
        if (this.watcher !== undefined) {
            window.clearInterval(this.watcher);
            this.watcher = undefined;
        }
    }

    /**
     * Navega si el paso vive en otra ruta y espera —poco— a que aparezca el ancla.
     * Shepherd espera esta promesa antes de posicionar el globo.
     */
    private async prepare(step: TourStep): Promise<void> {
        if (step.route && !this.atRoute(step.route)) {
            await this.router.navigateByUrl(step.route);
        }
        if (!step.anchor) {
            return;
        }
        const wait = step.waitMs ?? (step.route ? ROUTE_WAIT_MS : DEFAULT_WAIT_MS);
        const deadline = Date.now() + wait;
        while (Date.now() < deadline && !this.findAnchor(step.anchor)) {
            await this.delay(POLL_INTERVAL_MS);
        }
    }

    /**
     * Comparación exacta, sin query ni fragmento: un paso del listado tiene que
     * devolver a la persona al listado aunque esté parada en el detalle, y
     * `startsWith` no lo haría porque el detalle cuelga de la misma ruta.
     */
    private atRoute(route: string): boolean {
        const path = this.router.url.split(/[?#]/)[0].replace(/\/$/, '');
        return path === route.replace(/\/$/, '');
    }

    private buildText(
        tour: Tour,
        step: TourStep,
        position: number,
        total: number,
        isFirst: boolean,
        isGate: boolean,
    ): string {
        const meta = `
            <div class="tour-meta">
                <span class="tour-meta__tour"><i class="${tour.icon}"></i>${tour.title}</span>
                <span class="tour-meta__count">${position} / ${total}</span>
            </div>`;
        // Pantalla vacía: no hay nada que abrir, así que ni se pide ni se promete
        // que el tour avanzará solo. Se dice qué pasa y por dónde seguir.
        if (step.emptyBody && step.emptyAnchor && this.findAnchor(step.emptyAnchor)) {
            const keysEmpty = isFirst ? this.keysNote() : '';
            return (
                `${meta}<div class="tour-body">${step.emptyBody}</div>` +
                `<div class="tour-hint"><i class="pi pi-info-circle"></i><span>` +
                `Si no hay nada que abrir, usa <b>Omitir</b> y el tour sigue.` +
                `</span></div>${keysEmpty}`
            );
        }

        const hint = step.hint
            ? `<div class="tour-hint"><i class="pi pi-info-circle"></i><span>${step.hint}</span></div>`
            : '';
        // En una puerta la espera se dice, no se sufre: sin esto el globo parece
        // trabado cuando en realidad está esperando el clic.
        const waiting = isGate
            ? `<div class="tour-waiting">
                   <span class="tour-waiting__dot"></span>
                   <span>Te espero aquí: el tour sigue solo en cuanto lo hagas.</span>
               </div>`
            : '';
        const keys = isFirst ? this.keysNote() : '';
        return `${meta}<div class="tour-body">${step.body}</div>${hint}${waiting}${keys}`;
    }

    private keysNote(): string {
        return `<div class="tour-keys">Avanza con los botones. También sirven las flechas
            <b>&larr;</b> <b>&rarr;</b>, y <b>Esc</b> para salir.</div>`;
    }

    /** Shepherd no conoce «center»: un paso centrado simplemente no lleva `attachTo`. */
    private placement(placement?: TourPlacement): 'top' | 'bottom' | 'left' | 'right' {
        return placement && placement !== 'center' ? placement : 'bottom';
    }

    private findAnchor(anchor: string): HTMLElement | undefined {
        const candidates = Array.from(
            document.querySelectorAll<HTMLElement>(`[data-tour="${anchor}"]`),
        ).filter((element) => element.offsetWidth > 0 || element.offsetHeight > 0);
        // Si hay varios (una tabla y su versión en diálogo), gana el último montado.
        return candidates.length ? candidates[candidates.length - 1] : undefined;
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    private seenKey(tourId: string): string {
        return `${SEEN_PREFIX}:${tourId}:${SEEN_VERSION}`;
    }
}
